import * as React from "react"
import { useState, useEffect, useCallback, useContext, useMemo } from "react"
import type { User } from "firebase/auth"
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from "firebase/auth"
import axios from "axios"
import posthog from "posthog-js"
import { capture, captureError, captureInteraction } from "../utils/analytics"
import { getFirebaseAuth, isFirebaseConfigured } from "../utils/firebase"
import { paymentsApiUrl } from "../utils/api"

export type AccountCredits = {
  remainingConversions: number
  includedConversions: number
  usedConversions: number
}

type AuthContextValue = {
  user: User | null
  loading: boolean
  authReady: boolean
  credits: AccountCredits | null
  creditsLoading: boolean
  refreshCredits: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined)

async function syncUserToBackend(user: User): Promise<void> {
  const idToken = await user.getIdToken()
  await axios.post(paymentsApiUrl(), {
    action: "register_user",
    idToken,
  })
}

async function fetchCredits(user: User): Promise<AccountCredits | null> {
  const idToken = await user.getIdToken()
  const { data } = await axios.post<{ remainingConversions: number; includedConversions: number; usedConversions: number }>(
    paymentsApiUrl(),
    { action: "get_account", idToken }
  )
  return {
    remainingConversions: data.remainingConversions ?? 0,
    includedConversions: data.includedConversions ?? 0,
    usedConversions: data.usedConversions ?? 0,
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [credits, setCredits] = useState<AccountCredits | null>(null)
  const [creditsLoading, setCreditsLoading] = useState(false)
  const authReady = isFirebaseConfigured()

  const refreshCredits = useCallback(async () => {
    const auth = getFirebaseAuth()
    const u = auth?.currentUser
    if (!u) {
      setCredits(null)
      return
    }
    setCreditsLoading(true)
    try {
      const next = await fetchCredits(u)
      setCredits(next)
    } catch (e) {
      console.warn("Failed to load credits:", e)
      captureError(e, "credits_load_failed")
      capture("Credits Load Failed", {})
      setCredits(null)
    } finally {
      setCreditsLoading(false)
    }
  }, [])

  useEffect(() => {
    const auth = getFirebaseAuth()
    if (!auth) {
      setLoading(false)
      return
    }
    const unsub = onAuthStateChanged(auth, async (next) => {
      setUser(next)
      if (next) {
        const posthogKey = process.env.GATSBY_POSTHOG_KEY || "phc_AGuqGBfc85fuXm6iRcJ9QvcvXiWtky6quDHGNQLSBBUr"
        if (posthogKey) {
          posthog.identify(next.uid, {
            ...(next.email ? { email: next.email } : {}),
          })
        }
        captureInteraction("auth_state_changed", { state: "signed_in" })
        try {
          await syncUserToBackend(next)
        } catch (e) {
          console.warn("Backend user sync failed:", e)
          captureError(e, "backend_user_sync_failed")
        }
        await refreshCredits()
      } else {
        const posthogKey = process.env.GATSBY_POSTHOG_KEY || "phc_AGuqGBfc85fuXm6iRcJ9QvcvXiWtky6quDHGNQLSBBUr"
        if (posthogKey) {
          posthog.reset()
        }
        captureInteraction("auth_state_changed", { state: "signed_out" })
        setCredits(null)
      }
      setLoading(false)
    })
    return () => unsub()
  }, [refreshCredits])

  const signOut = useCallback(async () => {
    const auth = getFirebaseAuth()
    if (auth) await firebaseSignOut(auth)
    setCredits(null)
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      authReady,
      credits,
      creditsLoading,
      refreshCredits,
      signOut,
    }),
    [user, loading, authReady, credits, creditsLoading, refreshCredits, signOut]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return ctx
}
